// ItemRenderer.tsx
import React, { memo, useCallback, useState } from "react";
import { Collapse } from "antd";
import type { CollapseProps } from "antd";
import type { DraggableAttributes } from "@dnd-kit/core";
import type { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { v4 as uuidv4 } from "uuid";
import {
  SurveyItem,
  QuestionData,
  QuestionType,
} from "../../../../types/survey";
import { SectionEditor } from "../SectionEditor";
import { QuestionEditor } from "../QuestionEditor";

interface ValidationError {
  itemId: string;
  field: "title" | "questionTitle";
  message: string;
}

interface ItemRendererProps {
  item: SurveyItem;
  index: number;
  onChange: (item: SurveyItem) => void;
  onDelete: () => void;
  onCopy: (item: SurveyItem, insertAfterIndex?: number) => void;
  onAddQuestion?: (type: QuestionType) => void;
  onUpdateQuestion?: (
    questionId: string,
    updates: Partial<QuestionData>,
  ) => void;
  onDeleteQuestion?: (questionId: string) => void;
  onCopyQuestionInSection?: (questionId: string) => void;
  onReorderQuestions?: (questionIds: string[]) => void;
  dragHandleProps?: {
    attributes: DraggableAttributes;
    listeners: SyntheticListenerMap | undefined;
  };
  availableQuestions?: Array<{ id: string; title: string }>;
  validationErrors?: ValidationError[];
}

const ItemRendererComponent: React.FC<ItemRendererProps> = ({
  item,
  index,
  onChange,
  onDelete,
  onCopy,
  onAddQuestion,
  onUpdateQuestion,
  onDeleteQuestion,
  onCopyQuestionInSection,
  onReorderQuestions,
  dragHandleProps,
  availableQuestions,
  validationErrors = [],
}) => {
  const [activeKeys, setActiveKeys] = useState<string[]>([item.id]);

  const isExpanded = activeKeys.includes(item.id);

  const handleQuestionChange = useCallback(
    (updates: Partial<QuestionData>) => {
      onChange({
        ...item,
        question: { ...item.question!, ...updates },
      });
    },
    [item, onChange],
  );

  const handleToggle = () => {
    if (isExpanded) {
      setActiveKeys([]);
    } else {
      setActiveKeys([item.id]);
    }
  };

  const handleCopyQuestion = useCallback(() => {
    if (!item.question) return;

    const copiedQuestion: QuestionData = {
      ...item.question,
      id: uuidv4(),
      title: `${item.question.title} (Copy)`,
      answers: item.question.answers?.map((a) => ({
        ...a,
        id: uuidv4(),
      })),
    };

    const copiedItem: SurveyItem = {
      ...item,
      id: uuidv4(),
      question: copiedQuestion,
    };

    onCopy(copiedItem);
  }, [item, onCopy]);

  const handleCopySection = useCallback(() => {
    if (!item.section) return;

    const originalId = item;
    console.log("id", originalId);

    const copiedSection = {
      ...item.section,
      id: uuidv4(),
      title: `${item.section.title} (Copy)`,
      questions: item.section.questions.map((q) => ({
        ...q,
        id: uuidv4(),
        answers: q.answers?.map((a) => ({
          ...a,
          id: uuidv4(),
        })),
      })),
    };

    const copiedItem: SurveyItem = {
      ...item,
      id: uuidv4(),
      section: copiedSection,
    };

    onCopy(copiedItem, index);
  }, [item, onCopy, index]);

  if (item.question) {
    const items: CollapseProps["items"] = [
      {
        key: item.id,
        label: (
          <div onClick={handleToggle}>
            <QuestionEditor.Header
              question={item.question}
              isExpanded={isExpanded}
              onDelete={onDelete}
              onCopy={handleCopyQuestion}
              dragHandleProps={dragHandleProps}
              hasError={validationErrors.some(
                (e) => e.field === "questionTitle",
              )}
            />
          </div>
        ),
        children: (
          <QuestionEditor.Content
            question={item.question}
            onChange={handleQuestionChange}
            onDelete={onDelete}
            onCopy={handleCopyQuestion}
            availableQuestions={availableQuestions}
            error={
              validationErrors.find((e) => e.field === "questionTitle")?.message
            }
          />
        ),
      },
    ];

    return (
      <Collapse
        activeKey={activeKeys}
        className="custom-collapse-question"
        onChange={(keys) => setActiveKeys(keys as string[])}
        bordered
        expandIcon={() => null}
        items={items}
        styles={{
          root: {
            backgroundColor: "#F5F5F5",
            overflow: "hidden",
            border: "none",
          },
          body: { backgroundColor: "#F5F5F5" },
        }}
      />
    );
  }
  if (item.section) {
    const items: CollapseProps["items"] = [
      {
        key: item.id,
        label: (
          <div onClick={handleToggle}>
            <SectionEditor.Header
              section={item.section}
              isExpanded={isExpanded}
              onDelete={onDelete}
              onCopy={handleCopySection}
              dragHandleProps={dragHandleProps}
              hasError={validationErrors.some((e) => e.field === "title")}
              onChange={(updates) =>
                onChange({ ...item, section: { ...item.section!, ...updates } })
              }
            />
          </div>
        ),
        children: (
          <SectionEditor.Content
            section={item.section}
            onChange={(updates) =>
              onChange({
                ...item,
                section: { ...item.section!, ...updates },
              })
            }
            onDelete={onDelete}
            onAddQuestion={onAddQuestion || (() => { })}
            onUpdateQuestion={onUpdateQuestion || (() => { })}
            onDeleteQuestion={onDeleteQuestion || (() => { })}
            onCopyQuestion={onCopyQuestionInSection || (() => { })}
            onReorderQuestions={onReorderQuestions}
            availableQuestions={availableQuestions}
            sectionError={
              validationErrors.find((e) => e.field === "title")?.message
            }
            lastQuestionError={
              validationErrors.find((e) => e.field === "questionTitle")?.message
            }
          />
        ),
      },
    ];

    return (
      <Collapse
        activeKey={activeKeys}
        className="custom-collapse"
        onChange={(keys) => setActiveKeys(keys as string[])}
        bordered
        expandIcon={() => null}
        items={items}
        styles={{
          root: {
            backgroundColor: "#FFFFFF",
            overflow: "hidden",
            border: "none",
          },
        }}
      // style={{ marginBottom: 16 }}
      />
    );
  }

  return null;
};

export const ItemRenderer = memo(ItemRendererComponent, (prev, next) => {
  return (
    prev.item === next.item && prev.validationErrors === next.validationErrors
  );
});
